import { useCallback, useEffect, useLayoutEffect, useState } from "react";

// Positions a floating panel against its trigger button.
//
// Everything here is `position: fixed` measured off the trigger's viewport rect,
// which is what lets the caller render the panel through a portal: no ancestor's
// `overflow` can clip it, and it can never widen the page. The box is clamped to
// the viewport on both axes and flips above the trigger when there isn't room
// below.
//
// Returns `null` until the first measurement, so callers should render the panel
// only once a position exists.

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

export default function useAnchoredPosition(
  open,
  triggerRef,
  {
    // Width is derived from the trigger when `matchTrigger`, else it's `minWidth`.
    minWidth = 200,
    maxWidth = 340,
    matchTrigger = true,
    maxHeight = 320,
    minHeight = 160,
    // Which trigger edge the panel lines up with before clamping.
    align = "left",
    gap = 6,
    // Below this much free space underneath, prefer flipping above.
    flipThreshold = 180,
  } = {}
) {
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const width = clamp(
      matchTrigger ? r.width : minWidth,
      Math.min(minWidth, vw - 16),
      Math.min(maxWidth, vw - 16)
    );
    const rawLeft = align === "right" ? r.right - width : r.left;
    const left = clamp(rawLeft, 8, Math.max(8, vw - 8 - width));

    const below = vh - r.bottom - gap - 6;
    const above = r.top - gap - 6;
    const openUp = below < flipThreshold && above > below;

    setPos({
      left,
      width,
      maxHeight: Math.max(minHeight, Math.min(maxHeight, openUp ? above : below)),
      top: openUp ? undefined : r.bottom + gap,
      bottom: openUp ? vh - r.top + gap : undefined,
    });
  }, [triggerRef, minWidth, maxWidth, matchTrigger, maxHeight, minHeight, align, gap, flipThreshold]);

  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onChange = () => place();

    // Subscribe to every scrollable ancestor of the trigger, not just window:
    // the portal shell scrolls its <main>, not the document, and a capture-phase
    // window listener doesn't reliably see that.
    const targets = [window];
    for (let node = triggerRef.current?.parentElement; node; node = node.parentElement) {
      const cs = window.getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(`${cs.overflowY} ${cs.overflowX}`)) targets.push(node);
    }

    targets.forEach((t) => t.addEventListener("scroll", onChange, { passive: true }));
    window.addEventListener("resize", onChange);
    return () => {
      targets.forEach((t) => t.removeEventListener("scroll", onChange));
      window.removeEventListener("resize", onChange);
    };
  }, [open, place, triggerRef]);

  return pos;
}
